import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";

const GROUPS = [
  { key: "contacts", label: "Contacts", route: (id) => `/app/contacts/${id}` },
  { key: "catalog", label: "Catalogue & Matériel", route: (id) => `/app/stock?open=${id}` },
  { key: "events", label: "Événements", route: (id) => `/app/evenements/${id}` },
  { key: "venues", label: "Lieux", route: (id) => `/app/lieux?open=${id}` },
  { key: "quotes", label: "Devis", route: (id) => `/app/devis/${id}` },
];

export default function GlobalSearch({ variant, compact = false, onExpand }) {
  const inSidebar = variant === "sidebar";
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const v = q.trim();
    if (v.length < 2) { setRes(null); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: v } }); setRes(data); setOpen(true); }
      catch { setRes(null); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (route) => { setOpen(false); setQ(""); setRes(null); navigate(route); };
  const total = res ? GROUPS.reduce((s, g) => s + (res[g.key]?.length || 0), 0) : 0;

  if (compact) {
    return (
      <button data-testid="global-search-compact" onClick={onExpand} title="Rechercher"
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-800 bg-zinc-900/60 text-zinc-400 transition-colors hover:text-[#FF6A00]">
        <Search className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div ref={boxRef} className={inSidebar ? "relative w-full" : "relative hidden max-w-md flex-1 sm:block"}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (res) setOpen(true); }}
        placeholder={inSidebar ? "Rechercher…" : "Rechercher partout : contact, matériel, devis, événement, lieu…"}
        aria-label="Recherche globale"
        className={`w-full ${inSidebar ? "h-10 rounded-xl" : "rounded-full py-2"} border border-zinc-800 bg-zinc-900/60 pl-10 pr-4 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF6A00]/40 focus:outline-none`}
      />
      {open && (
        <div data-testid="global-search-results" className={`absolute z-50 mt-2 max-h-[70vh] ${inSidebar ? "left-0 w-[22rem] max-w-[85vw]" : "w-full"} overflow-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-2 shadow-2xl`}>
          {loading && (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-zinc-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#FF6A00]/30 border-t-[#FF6A00]" />Recherche…
            </div>
          )}
          {!loading && total === 0 && <div className="px-3 py-6 text-center text-sm text-zinc-500">Aucun résultat pour « {q} »</div>}
          {!loading && total > 0 && GROUPS.map((g) => {
            const rows = res?.[g.key] || [];
            if (!rows.length) return null;
            return (
              <div key={g.key} className="mb-1">
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{g.label}</p>
                {rows.map((r) => (
                  <button key={r.id} data-testid={`search-result-${g.key}-${r.id}`} onClick={() => go(g.route(r.id))}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-zinc-800">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-white">{r.label || "—"}</span>
                      {r.sublabel && <span className="block truncate text-xs text-zinc-500">{r.sublabel}</span>}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
